import WaterPumpAnimation from '@/components/modules/WaterPumpAnimation';
import type { MotorRunTime, MotorStatusDetail } from '@/types/site';
import { formatDuration } from 'date-fns';
import { ko } from 'date-fns/locale';

interface MotorStatusData {
  motorStatus: string;
  motorRunningTime: number;
  motorStoppedTime: number;
  cumulativeHours: number;
  todayHours: number;
  dailyHours: number;
  weeklyHours: number;
}

interface MotorStatusSectionProps {
  data: MotorStatusData;
  motorStatus: MotorStatusDetail;
  motorActivateCount: number;
  motorRunTimeData: MotorRunTime;
  isLoading?: boolean;
  expanded?: boolean;
}

const formatTime = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

// 분 단위를 "00시간 00분" 형식으로 변환
const formatMinutesToKorean = (minutes: number | undefined) => {
  if (minutes === undefined || minutes === null) return '0시간 0분';

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  // date-fns의 formatDuration은 0인 경우 빈 문자열을 반환할 수 있음
  // 명시적으로 처리
  if (hours === 0 && mins === 0) return '0시간 0분';
  if (hours === 0) return `${mins}분`;
  if (mins === 0) return `${hours}시간`;

  return formatDuration(
    { hours, minutes: mins },
    { format: ['hours', 'minutes'], locale: ko }
  );
};

export default function MotorStatusSection({
  // motorActivateCount,
  motorRunTimeData,
  motorStatus,
  expanded = true,
  data,
}: Readonly<MotorStatusSectionProps>) {
  // 메인 모터 상태를 애니메이션 타입으로 변환
  const getMainMotorAnimationStatus = (): '운전' | '정지' | '대기' => {
    if (motorStatus.mainMotor) return '운전';
    return '정지';
  };

  // 개별 모터 상태를 애니메이션 타입으로 변환
  // const getIndividualMotorStatus = (
  //   isRunning: boolean
  // ): '운전' | '정지' | '대기' => {
  //   return isRunning ? '운전' : '정지';
  // };

  const timeGridItems = expanded
    ? [
        {
          label: '오늘 동작 시간',
          value: formatMinutesToKorean(motorRunTimeData?.today),
        },
        {
          label: '어제 동작 시간',
          value: formatMinutesToKorean(motorRunTimeData?.yesterday),
        },

        {
          label: '이번주 동작 시간',
          value: formatMinutesToKorean(motorRunTimeData?.weekly),
        },
        {
          label: '이번달 동작 시간',
          value: formatMinutesToKorean(motorRunTimeData?.monthly),
        },
        {
          label: '누적 시간',
          value: formatMinutesToKorean(motorRunTimeData?.total),
        },
      ]
    : [
        {
          label: '오늘 동작 시간',
          value: formatMinutesToKorean(motorRunTimeData?.today),
        },
        {
          label: '어제 동작 시간',
          value: formatMinutesToKorean(motorRunTimeData?.yesterday),
        },

        {
          label: '이번주 동작 시간',
          value: formatMinutesToKorean(motorRunTimeData?.weekly),
        },
        {
          label: '이번달 동작 시간',
          value: formatMinutesToKorean(motorRunTimeData?.monthly),
        },
        {
          label: '누적 시간',
          value: formatMinutesToKorean(motorRunTimeData?.total),
        },
      ];

  return (
    <div className="bg-gray-50 rounded-xl p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">모터 상태</h3>

      {/* 메인 모터 펌프 상태 애니메이션 */}
      {
        <div className="flex items-center justify-center mb-6">
          <div className="text-center">
            <WaterPumpAnimation
              status={getMainMotorAnimationStatus()}
              size="w-48 h-48"
              showWaterFlow={true}
            />
            <p className="text-sm text-gray-600 mt-2 font-medium">메인 모터</p>
          </div>
        </div>
      }

      {/* 개별 모터 상태 */}
      {/* <div className="mb-6">
        <h4 className="text-md font-medium text-gray-800 mb-3 text-center">
          개별 모터 상태
        </h4>
        <div className="flex justify-center items-center gap-6">
          {motorActivateCount && motorActivateCount > 0 && (
            <div className="text-center">
              <WaterPumpAnimation
                status={getIndividualMotorStatus(motorStatus.motor1)}
                size="w-20 h-20"
                showWaterFlow={false}
              />
              <p className="text-xs text-gray-600 mt-1 font-medium">모터 1</p>
              <p className="text-xs text-gray-500">
                {motorStatus.motor1 ? '운전 중' : '정지'}
              </p>
            </div>
          )}

          {motorActivateCount && motorActivateCount > 1 && (
            <div className="text-center">
              <WaterPumpAnimation
                status={getIndividualMotorStatus(motorStatus.motor2)}
                size="w-20 h-20"
                showWaterFlow={false}
              />
              <p className="text-xs text-gray-600 mt-1 font-medium">모터 2</p>
              <p className="text-xs text-gray-500">
                {motorStatus.motor2 ? '운전 중' : '정지'}
              </p>
            </div>
          )}

          {motorActivateCount && motorActivateCount > 2 && (
            <div className="text-center">
              <WaterPumpAnimation
                status={getIndividualMotorStatus(motorStatus.motor3)}
                size="w-20 h-20"
                showWaterFlow={false}
              />
              <p className="text-xs text-gray-600 mt-1 font-medium">모터 3</p>
              <p className="text-xs text-gray-500">
                {motorStatus.motor3 ? '운전 중' : '정지'}
              </p>
            </div>
          )}
        </div>
      </div> */}

      {/* 메인 모터 운전 시간 정보 */}
      {
        <div className="text-center mb-6">
          <p className="text-sm text-gray-600">
            {motorStatus.mainMotor
              ? `${formatTime(data.motorRunningTime)} 운전 중`
              : `${formatTime(data.motorStoppedTime)} 정지 중`}
          </p>
        </div>
      }

      {/* 운전 시간 통계 */}
      {
        <div className="grid grid-cols-1 gap-4">
          {timeGridItems.map((item, index) => (
            <div key={index} className="bg-white rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{item.label}</span>
                <span className="text-lg font-bold text-gray-900">
                  {item.value}
                </span>
              </div>
            </div>
          ))}
        </div>
      }
    </div>
  );
}
